import { FC, useEffect, useState } from 'react';

import { Portal } from '@headlessui/react';
import classNames from 'classnames';
import { isNil, isNull } from 'lodash';
import { usePopper } from 'react-popper';
import { twMerge } from 'tailwind-merge';

interface PopperContentProps {
  popperRef: any;
  popperStyles: any;
  popperAttributes: any;
  children: any;
  onPopperClick?: any;
  classOverride?: string;
}

export interface PopperProps {
  idsToClosePopper?: string[];
  isKeepPopperOpenAfterClick?: boolean;
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'auto';
  disabled?: boolean;
  popComponent: any;
  refComponent: any;
  trigger?: 'click' | 'hover';
  customClassName?: string;
  overlayClassName?: string;
  onRefComponentClick?: (event: any) => void;
  popperStyleOverride?: any;
  controlledIsOpen?: boolean; // Prop to control the popper's open state
  handleControlledIsOpen?: (isOpen: boolean) => void; // Callback function to handle changes to the popper's open state
  shouldUpdateOnContentChange?: boolean;
}

const PopperContent = (props: PopperContentProps) => {
  const {
    children,
    popperRef,
    popperStyles,
    popperAttributes,
    onPopperClick,
    classOverride,
  } = props;

  return (
    <div
      ref={popperRef}
      style={{ ...popperStyles, zIndex: 50 }}
      onClick={onPopperClick}
      {...popperAttributes}
      className={twMerge(
        classNames(
          'rounded-lg bg-modal-surface-default text-text-primary',
          classOverride,
        ),
      )}
    >
      {children}
    </div>
  );
};

const PopperPortal: FC<PopperProps> = ({
  idsToClosePopper = [],
  isKeepPopperOpenAfterClick = false,
  placement = 'top',
  disabled = false,
  popComponent,
  refComponent,
  trigger = 'click',
  customClassName = '',
  overlayClassName = '',
  onRefComponentClick,
  popperStyleOverride = {},
  controlledIsOpen,
  handleControlledIsOpen,
  shouldUpdateOnContentChange = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);

  useEffect(() => {
    if (!isNil(controlledIsOpen)) {
      setIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const { styles, attributes, forceUpdate } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers: [
        {
          name: 'flip',
          enabled: true,
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    },
  );

  useEffect(() => {
    if (
      !shouldUpdateOnContentChange ||
      !isOpen ||
      !popperElement ||
      !forceUpdate
    )
      return;

    const resizeObserver = new ResizeObserver(() => {
      forceUpdate();
    });

    resizeObserver.observe(popperElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, popperElement, forceUpdate]);

  const closePopper = () => {
    if (handleControlledIsOpen) {
      handleControlledIsOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  const onPopperToggle = (event: any) => {
    if (!popComponent) return;

    if (handleControlledIsOpen) {
      handleControlledIsOpen(!isOpen);
    } else {
      setIsOpen(!isOpen);
    }

    if (onRefComponentClick) onRefComponentClick(event);
    else event.stopPropagation();
  };

  const onPopperClick = (event: any) => {
    if (idsToClosePopper.length > 0) {
      // specific ids to trigger close

      if (idsToClosePopper.includes(event.target.id)) {
        closePopper();
      }

      event.stopPropagation();

      return;
    }
    if (isKeepPopperOpenAfterClick) {
      event.stopPropagation();

      return;
    } else {
      // trigger close on any click
      closePopper();
    }
  };

  const refComponentIsFunction = typeof refComponent === 'function';
  const popComponentIsFunction = typeof popComponent === 'function';

  return (
    <>
      <div
        ref={setReferenceElement}
        className={customClassName}
        onClick={trigger === 'click' && !disabled ? onPopperToggle : () => {}}
        onMouseEnter={
          trigger === 'hover' && !disabled ? () => setIsOpen(true) : () => {}
        }
        onMouseLeave={
          trigger === 'hover' && !disabled ? () => setIsOpen(false) : () => {}
        }
      >
        {refComponentIsFunction ? refComponent({ isOpen }) : refComponent}
      </div>
      <Portal>
        {!isNull(popComponent) && isOpen && (
          <>
            {trigger === 'click' ? (
              <div
                className={twMerge(
                  classNames(
                    'fixed left-0 top-0 z-50 h-screen w-screen',
                    overlayClassName,
                  ),
                )}
                onClick={onPopperToggle}
              >
                <PopperContent
                  popperAttributes={{ ...attributes.popper }}
                  popperRef={setPopperElement}
                  popperStyles={{ ...styles.popper, ...popperStyleOverride }}
                  onPopperClick={onPopperClick}
                  classOverride="border border-stroke-subtle shadow-modal"
                >
                  {popComponentIsFunction
                    ? popComponent({
                        height: referenceElement.clientHeight,
                        width: referenceElement.clientWidth,
                      })
                    : popComponent}
                </PopperContent>
              </div>
            ) : (
              <PopperContent
                popperAttributes={{ ...attributes.popper }}
                popperRef={setPopperElement}
                popperStyles={{
                  ...styles.popper,
                  zIndex: 50,
                  ...popperStyleOverride,
                }}
              >
                {popComponentIsFunction
                  ? popComponent({
                      height: referenceElement.clientHeight,
                      width: referenceElement.clientWidth,
                    })
                  : popComponent}
              </PopperContent>
            )}
          </>
        )}
      </Portal>
    </>
  );
};

export default PopperPortal;
